import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { firstValueFrom, forkJoin } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import {
  Auth,
  getMultiFactorResolver,
  multiFactor,
  MultiFactorInfo,
  MultiFactorResolver,
  onAuthStateChanged,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  signInWithEmailAndPassword,
  signOut,
  TotpMultiFactorGenerator,
  TotpSecret,
  User,
} from 'firebase/auth';

import { firebaseAuth } from '../firebase';
import { SNACKBAR_LOGIN_ERROR, SNACKBAR_USER_ERROR } from '../../locale/consts.en';
import { DatabaseService } from '../shared/database.service';
import { MasterDataService } from '../master-data/master-data-service';
import { SYSTEM_ADMIN_UUID, User as LoggedUser } from '../users/users.model';
import { UsersService } from '../users/users.service';

export interface Credentials {
  email: string;
  password: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private auth: Auth = firebaseAuth;
  private router = inject(Router);
  private snackBar = inject(MatSnackBar);
  private usersService = inject(UsersService);
  private masterDataService = inject(MasterDataService);
  private databaseService = inject(DatabaseService);

  readonly authStep = signal<'login' | 'mfa_selection' | 'mfa' | 'totp_setup'>('login');
  readonly availableHints = signal<MultiFactorInfo[]>([]);
  private mfaResolver = signal<MultiFactorResolver | null>(null);
  private verificationId = signal<string | null>(null);
  readonly selectedHint = signal<MultiFactorInfo | null>(null);
  private recaptchaVerifier: RecaptchaVerifier | null = null;
  readonly totpSecret = signal<TotpSecret | null>(null);
  readonly qrCodeUrl = signal<string | null>(null);
  readonly user = signal<User | null>(null);
  readonly token = signal<string | null>(null);

  readonly userName = computed(() => {
    const user = this.user();
    return user ? (user.displayName ?? user.email) : null;
  });

  readonly isMFAEnabled = computed(() => this.availableHints().length > 0);
  readonly phoneHints = computed(() =>
    this.availableHints().filter(
      (h) => h.factorId === PhoneMultiFactorGenerator.FACTOR_ID,
    ),
  );
  readonly hasSmsAlternatives = computed(() => this.phoneHints().length > 0);

  get userId() {
    return this.user() ? this.user()!.uid : null;
  }

  // LOGIN //

  async login({ email, password }: Credentials): Promise<'SUCCESS' | 'MFA_SETUP'> {
    try {
      await signInWithEmailAndPassword(this.auth, email, password);

      const currentUser = this.auth.currentUser;
      const enrolledFactors = currentUser
        ? multiFactor(currentUser).enrolledFactors
        : [];

      if (enrolledFactors.length === 0) {
        const setupStatus = await this.verifyUserAndCheckMfaSetup(currentUser!);
        if (setupStatus === 'MFA_SETUP') {
          return 'MFA_SETUP';
        }
      }

      await this.handleSuccessfulAuth();
      return 'SUCCESS';
    } catch (error: any) {
      if (error.code === 'auth/multi-factor-auth-required') {
        const resolver = getMultiFactorResolver(this.auth, error);
        this.mfaResolver.set(resolver);
        this.availableHints.set(resolver.hints);

        // TOTP always first
        const totpHint = resolver.hints.find(
          (h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID,
        );

        if (totpHint) {
          this.selectedHint.set(totpHint);
          this.authStep.set('mfa');
          return Promise.reject({ status: 'MFA_REQUIRED' });
        }
        // Only phone with SMS
        else {
          const phones = this.phoneHints();
          if (phones.length === 1) {
            this.authStep.set('mfa');
            await this.sendSmsCode(phones[0]);
            return Promise.reject({ status: 'MFA_REQUIRED' });
          } else {
            this.authStep.set('mfa_selection');
            return Promise.reject({ status: 'MFA_SELECTION_REQUIRED' });
          }
        }
      } else {
        if (error?.status !== 'UNAUTHORIZED') {
          this.showLoginError();
        }
        throw error;
      }
    }
  }

  // MFA VERIFICATION //

  async verifyMfaCode(code: string): Promise<'SUCCESS' | 'MFA_SETUP'> {
    const resolver = this.mfaResolver();
    const vId = this.verificationId();

    if (!resolver || !vId) {
      return Promise.reject($localize`No verification data`);
    }
    try {
      const credential = PhoneAuthProvider.credential(vId, code);
      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(credential);
      await resolver.resolveSignIn(multiFactorAssertion);

      const currentUser = this.auth.currentUser;
      const enrolledFactors = currentUser
        ? multiFactor(currentUser).enrolledFactors
        : [];
      const hasTotp = enrolledFactors.some(
        (f) => f.factorId === TotpMultiFactorGenerator.FACTOR_ID,
      );

      // If login success and TOTP is not enrolled yet - force TOTP setup
      if (!hasTotp) {
        const setupStatus = await this.verifyUserAndCheckMfaSetup(currentUser!);
        if (setupStatus === 'MFA_SETUP') {
          return 'MFA_SETUP';
        }
      }
      await this.handleSuccessfulAuth();
      return 'SUCCESS';
    } catch (error) {
      console.error('MFA Verify Error', error);
      this.showMfaError($localize`Invalid code from the text message.`);
      throw error;
    }
  }

  async verifyTotpCode(code: string): Promise<'SUCCESS'> {
    const resolver = this.mfaResolver();
    const hint = this.selectedHint();

    if (
      !resolver ||
      !hint ||
      hint.factorId !== TotpMultiFactorGenerator.FACTOR_ID
    ) {
      return Promise.reject($localize`No verification data`);
    }

    try {
      const multiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(
        hint.uid,
        code,
      );
      await resolver.resolveSignIn(multiFactorAssertion);
      await this.handleSuccessfulAuth();
      return 'SUCCESS';
    } catch (error) {
      console.error('TOTP Verify Error', error);
      this.showMfaError($localize`Invalid authenticator code.`);
      throw error;
    }
  }

  async verifyAndEnrollTotp(code: string): Promise<void> {
    const secret = this.totpSecret();
    const currentUser = this.auth.currentUser;
    if (!secret || !currentUser) {
      return Promise.reject($localize`No verification data`);
    }
    try {
      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, code);
      await multiFactor(currentUser).enroll(assertion, $localize`Authenticator App`);
      await this.handleSuccessfulAuth();
    } catch (error) {
      console.error('Verification error', error);
      this.showMfaError($localize`Invalid code. Please try again.`);
      throw error;
    }
  }

  // SMS FALLBACK AND SENDING //

  async sendSmsCode(selectedHint: MultiFactorInfo) {
    this.selectedHint.set(selectedHint);
    const resolver = this.mfaResolver();

    if (!resolver) return;
    const phoneInfoOptions = {
      multiFactorHint: selectedHint,
      session: resolver.session,
    };
    if (this.recaptchaVerifier) {
      this.recaptchaVerifier.clear();
    }

    const phoneAuthProvider = new PhoneAuthProvider(this.auth);

    this.recaptchaVerifier = new RecaptchaVerifier(this.auth, 'recaptcha-container', {
      size: 'invisible',
    });
    try {
      const vId = await phoneAuthProvider.verifyPhoneNumber(
        phoneInfoOptions,
        this.recaptchaVerifier,
      );
      this.verificationId.set(vId);
      this.authStep.set('mfa');
    } catch (error) {
      console.error('SMS send error', error);
      this.showMfaError($localize`Error sending the SMS code.`);
      this.resetMfaState();
      throw error;
    }
  }

  async resendSmsCode() {
    const hint = this.selectedHint();
    if (hint) {
      return this.sendSmsCode(hint);
    }
  }

  async switchToSmsFallback() {
    const phones = this.phoneHints();
    if (phones.length === 1) {
      this.authStep.set('mfa');
      await this.sendSmsCode(phones[0]);
    } else if (phones.length > 1) {
      this.authStep.set('mfa_selection');
    }
  }

  // STATE MANAGEMENT / AUTO AUTH //

  async skipMfaSetup(): Promise<void> {
    await this.handleSuccessfulAuth();
  }

  resetMfaState() {
    this.authStep.set('login');
    this.mfaResolver.set(null);
    this.verificationId.set(null);
    this.selectedHint.set(null);
    this.totpSecret.set(null);
    this.qrCodeUrl.set(null);
    if (this.recaptchaVerifier) {
      this.recaptchaVerifier.clear();
      this.recaptchaVerifier = null;
    }
  }

  logout() {
    signOut(this.auth).then(() => {
      this.masterDataService.clearMasterData();
      this.router.navigate(['/login']).then();
      this.token.set(null);
      this.user.set(null);
      this.resetMfaState();
    });
  }

  autoAuth() {
    onAuthStateChanged(
      this.auth,
      (user) => {
        if (user) {
          this.user.set(user);
          this.usersService
            .getUserUuid(user.uid)
            .pipe(
              take(1),
              switchMap((uuid) => this.usersService.getUserData(uuid)),
            )
            .subscribe((resp) => {
              if (this.isAuthorizedUser(resp)) {
                user.getIdToken().then((tokenValue) => this.token.set(tokenValue));
              } else {
                this.snackBar.open(SNACKBAR_USER_ERROR, '', { duration: 2000 });
                this.logout();
              }
            });
        } else {
          this.logout();
        }
      },
      (err) => {
        console.log('AutoAuth Error: ', err);
      },
    );
  }

  // PRIVATE (AUXILIARY) METHODS //

  private isAuthorizedUser(user: LoggedUser | null): boolean {
    if (user === null || user.enabled === false) {
      return false;
    }
    return user.roles?.map((r) => r.uuid)?.includes(SYSTEM_ADMIN_UUID) ?? false;
  }

  private async handleSuccessfulAuth() {
    this.user.set(this.auth.currentUser);
    const currentUser = this.user();
    if (currentUser) {
      const tokenValue = await currentUser.getIdToken();
      this.token.set(tokenValue);
    } else {
      this.token.set(null);
    }
    this.resetMfaState();
  }

  private async verifyUserAndCheckMfaSetup(
    currentUser: User,
  ): Promise<'MFA_SETUP' | 'CONTINUE'> {
    const config = await this.getUserConfigData(currentUser.uid);

    if (config) {
      if (!this.isAuthorizedUser(config.userConfig)) {
        this.logout();
        throw { status: 'UNAUTHORIZED' };
      }

      const { isMfaGlobalEnabled, isMfaAuthAppGlobalEnabled, ignoreMfa } = config;
      // If both globalConfig(security_enable_mfa, security_mfa_auth_app) variables are TRUE and we do not ignore the user -> FORCE TOTP
      if (isMfaGlobalEnabled && isMfaAuthAppGlobalEnabled && !ignoreMfa) {
        const canSetupMfa = await this.generateTotpSetup(currentUser);
        if (canSetupMfa) {
          return 'MFA_SETUP';
        }
      }
      // In any other case (ignoreMfa = true, or any globalConfig(security_enable_mfa, security_mfa_auth_app) = false)
      // allow the user to access the application freely.
    }
    return 'CONTINUE';
  }

  private async generateTotpSetup(currentUser: User): Promise<boolean> {
    try {
      const multiFactorSession = await multiFactor(currentUser).getSession();
      const secret = await TotpMultiFactorGenerator.generateSecret(multiFactorSession);

      this.totpSecret.set(secret);
      this.qrCodeUrl.set(
        secret.generateQrCodeUrl(currentUser.email || $localize`User`, 'AdaptiveServiceAdmin'),
      );
      this.authStep.set('totp_setup');
      return true;
    } catch (error: any) {
      console.warn(
        'MFA configuration failed. The service is likely disabled on the server:',
        error.code,
      );
      return false; // MFA not configured in firebase
    }
  }

  private async getUserConfigData(fbId: string) {
    try {
      const uuid = await firstValueFrom(this.usersService.getUserUuid(fbId).pipe(take(1)));

      if (!uuid) {
        throw new Error('No UUID !!');
      }

      const { userConfig, globalConfig } = await firstValueFrom(
        forkJoin({
          userConfig: this.usersService.getUserData(uuid),
          globalConfig: this.databaseService.loadConfig(),
        }),
      );

      const configMap: Record<string, string> = (globalConfig?.configMap as any) ?? {};
      const isMfaGlobalEnabled = JSON.parse(configMap.security_enable_mfa ?? 'false');
      const isMfaAuthAppGlobalEnabled = JSON.parse(configMap.security_mfa_auth_app ?? 'false');
      const isMfaSmsGlobalEnabled = JSON.parse(configMap.security_mfa_sms ?? 'false');
      const ignoreMfa = userConfig?.ignoreMfa;

      return {
        isMfaGlobalEnabled,
        isMfaAuthAppGlobalEnabled,
        isMfaSmsGlobalEnabled,
        ignoreMfa,
        userConfig,
      };
    } catch (error) {
      console.error('Error retrieving user data:', error);
      return null;
    }
  }

  private showLoginError() {
    const config = new MatSnackBarConfig();
    config.panelClass = ['app-snack-panel'];
    config.duration = 2500;
    config.verticalPosition = 'top';
    this.snackBar.open(SNACKBAR_LOGIN_ERROR, '', config);
  }

  private showMfaError(message: string) {
    this.snackBar.open(message, '', { duration: 2500, panelClass: 'app-snackbar-alert' });
  }
}
